// src/modulos/Parametros/CostCenterPage.js
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import {
    PageHeader,
    SearchBar,
    TableSkeleton,
    Pagination,
    FormModal,
    Toast
} from '../../components/common';
import styles from '../../components/common/TableSkeleton/TableSkeleton.module.css';
import { FiTrash2, FiEdit } from 'react-icons/fi';

const COST_CENTER_COLLECTION = 'parameters/data/cost-center';
const ITEMS_PER_PAGE = 10;

const CostCenterPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('centro_de_custo');
    const { data: costCenters, loading, error, addDocument, updateDocument, deleteDocument } = useFirestore(COST_CENTER_COLLECTION);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [formLoading, setFormLoading] = useState(false);
    const [toast, setToast] = useState(null);

    // Form state
    const [parentCostCenterId, setParentCostCenterId] = useState('');
    const [description, setDescription] = useState('');

    // Build hierarchical structure for display
    const buildHierarchy = (items, parentId = null, level = 0) => {
        return items
            .filter(item => item.parentCostCenterId === parentId)
            .sort((a, b) => (a.description || '').localeCompare(b.description || ''))
            .map(item => ({
                ...item,
                level,
                children: buildHierarchy(items, item.id, level + 1)
            }));
    };

    // Flatten hierarchy for table display with indentation
    const flattenHierarchy = (items, flatList = []) => {
        items.forEach(item => {
            flatList.push(item);
            if (item.children && item.children.length > 0) {
                flattenHierarchy(item.children, flatList);
            }
        });
        return flatList;
    };

    // Build hierarchical data
    const hierarchicalData = useMemo(() => {
        if (!costCenters.length) return [];
        return buildHierarchy(costCenters);
    }, [costCenters]);

    // Filter and flatten based on search term
    const filteredAndFlattened = useMemo(() => {
        const flattened = flattenHierarchy([...hierarchicalData]);

        if (!searchTerm.trim()) return flattened;

        return flattened.filter(item =>
            item.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [hierarchicalData, searchTerm]);

    // Pagination
    const totalPages = Math.ceil(filteredAndFlattened.length / ITEMS_PER_PAGE);
    const paginatedItems = filteredAndFlattened.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Get parent cost center options (excluding the current item when editing to avoid circular reference)
    const getParentOptions = () => {
        if (!costCenters.length) return [];

        const excludeId = editingItem?.id;

        // Build a flat list of options with indentation representation
        const buildOptions = (items, level = 0, options = []) => {
            items.forEach(item => {
                if (item.id !== excludeId) {
                    const prefix = '— '.repeat(level);
                    options.push({
                        id: item.id,
                        label: `${prefix}${item.description}`,
                        level
                    });
                    if (item.children && item.children.length) {
                        buildOptions(item.children, level + 1, options);
                    }
                } else if (item.children && item.children.length) {
                    // If editing, also skip children to prevent circular reference
                    buildOptions(item.children, level + 1, options);
                }
            });
            return options;
        };

        return buildOptions(hierarchicalData);
    };

    const parentOptions = getParentOptions();

    const handleOpenModal = (item = null) => {
        setEditingItem(item);
        if (item) {
            setParentCostCenterId(item.parentCostCenterId || '');
            setDescription(item.description || '');
        } else {
            setParentCostCenterId('');
            setDescription('');
        }
        setIsModalOpen(true);
    };

    const resetForm = () => {
        setParentCostCenterId('');
        setDescription('');
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!description.trim()) {
            showToast(t('costCenter.form.descriptionRequired'), 'error');
            return;
        }

        setFormLoading(true);

        const payload = {
            parentCostCenterId: parentCostCenterId || null,
            description: description.trim(),
            updatedAt: new Date()
        };

        try {
            if (editingItem) {
                await updateDocument(editingItem.id, payload);
                showToast(t('costCenter.messages.updateSuccess'), 'success');
            } else {
                await addDocument({ ...payload, createdAt: new Date() });
                showToast(t('costCenter.messages.createSuccess'), 'success');
            }
            setIsModalOpen(false);
            resetForm();
        } catch (err) {
            console.error("Erro ao salvar Centro de Custo:", err);
            showToast(t('costCenter.messages.saveError'), 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (id) => {
        // Check if this cost center has children before deleting
        const hasChildren = costCenters.some(cc => cc.parentCostCenterId === id);

        if (hasChildren) {
            showToast(t('costCenter.messages.hasChildren'), 'warning');
            return;
        }

        if (window.confirm(t('costCenter.messages.deleteConfirm'))) {
            try {
                await deleteDocument(id);
                showToast(t('costCenter.messages.deleteSuccess'), 'success');
            } catch (err) {
                console.error("Erro ao excluir Centro de Custo:", err);
                showToast(t('costCenter.messages.deleteError'), 'error');
            }
        }
    };

    // Helper to render indentation
    const renderIndentation = (level) => {
        if (level === 0) return null;
        return (
            <span style={{ display: 'inline-block', width: level * 24, marginRight: 4 }}>
                {'↳'}
            </span>
        );
    };

    return (
        <div style={{ padding: '24px' }}>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <PageHeader
                title={t('costCenter.title')}
                actionText={t('costCenter.newCostCenter')}
                onAction={() => handleOpenModal()}
                disabled={!can.create}
                showAction={can.create}
            />

            <SearchBar
                placeholder={t('costCenter.searchPlaceholder')}
                onSearch={(term) => {
                    setSearchTerm(term);
                    setCurrentPage(1);
                }}
            />

            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>{t('costCenter.table.description')}</th>
                            <th>{t('costCenter.table.parentCostCenter')}</th>
                            <th style={{ width: '150px' }}>{t('costCenter.table.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="3">
                                    <TableSkeleton columns={3} rows={5} />
                                </td>
                            </tr>
                        ) : paginatedItems.length > 0 ? (
                            paginatedItems.map(item => {
                                // Find parent description
                                const parent = costCenters.find(cc => cc.id === item.parentCostCenterId);
                                return (
                                    <tr key={item.id}>
                                        <td>
                                            {renderIndentation(item.level)}
                                            <strong>{item.description}</strong>
                                        </td>
                                        <td>{parent?.description || '—'}</td>
                                        <td>
                                            {can.edit && (
                                                <button
                                                    onClick={() => handleOpenModal(item)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '4px 8px',
                                                        color: '#1976d2'
                                                    }}
                                                >
                                                    <FiEdit />
                                                </button>
                                            )}
                                            {can.delete && (
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '4px 8px',
                                                        color: '#d32f2f'
                                                    }}
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="3" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    {searchTerm ? t('costCenter.table.emptySearch') : t('costCenter.table.empty')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredAndFlattened.length}
                onPageChange={setCurrentPage}
                itemsLabel={t('costCenter.pagination.items')}
            />

            <FormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleSave}
                title={editingItem ? t('costCenter.editCostCenterTitle') : t('costCenter.newCostCenterTitle')}
                isLoading={formLoading}
                submitText={formLoading ? t('costCenter.form.saving') : t('costCenter.form.save')}
                cancelText={t('costCenter.form.cancel')}
                maxWidth="560px"
            >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                    <div className={styles.formGroup}>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '4px' }}>
                            {t('costCenter.form.parentCostCenter')}
                        </label>
                        <select
                            className={styles.select}
                            value={parentCostCenterId}
                            onChange={e => setParentCostCenterId(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: '#fff'
                            }}
                        >
                            <option value="">{t('costCenter.form.parentNone')}</option>
                            {parentOptions.map(opt => (
                                <option key={opt.id} value={opt.id}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <small style={{ color: '#666', fontSize: 11, marginTop: 4, display: 'block' }}>
                            {t('costCenter.form.parentHint')}
                        </small>
                    </div>

                    <div className={styles.formGroup}>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '4px' }}>
                            {t('costCenter.form.description')}
                        </label>
                        <input
                            type="text"
                            className={styles.input}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder={t('costCenter.form.descriptionPlaceholder')}
                            required
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                        />
                    </div>
                </div>
            </FormModal>
        </div>
    );
};

export default CostCenterPage;